import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BsFillCheckCircleFill,
  BsPlus,
  BsXLg,
  BsXCircle,
} from "react-icons/bs";
import { MdAccessTimeFilled } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { FaBell } from "react-icons/fa";
import { IoMdPower } from "react-icons/io";
import Select from "react-select";
import { AddParticipants, RemovePlayerModal, RefreshConnection } from "./index";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { constant, table } from "@/utils/constant";
import {
  emitResetGameLink,
  emitCancelHelpRequest,
} from "@/utils/socketHelpers";
import { toast } from "react-toastify";

function ParticipantsSelect({
  allTeamData,
  teamData,
  teamParticipants,
  unassigned,
  role,
  state,
  socket,
  sessionData,
  participantData,
  onOptimisticReassign,
  onOptimisticUnassign,
}) {
  const [modify, setModify] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [roleId, setRoleId] = useState();
  const [value, setValue] = useState(undefined);
  const [isAddParticipantAssign, setIsAddParticipantAssign] = useState();
  const [removeParticipant, setRemoveParticipant] = useState(false);
  const [openRefreshconnectionModel, setOpenRefreshconnectionModel] =
    useState(false);
  const [showModal, setShowModal] = useState(false);
  const dropdownRef = useRef(null);
  const checkStatus = allTeamData?.filter((item) => item.current_status > 2);
  const toggleOpen = () => {
    setIsOpen(!isOpen);
    setRoleId(role?.role_id?.id);
  };

  const onSelectChange = (value) => {
    setIsOpen(false);
    setValue(value);

    // Store previous for revert on error
    const previous = {
      participantId: role?.participant_id?.id ?? null,
      first_name: role?.participant_id?.first_name ?? "",
      last_name: role?.participant_id?.last_name ?? "",
    };

    // Optimistic update: show in UI immediately
    const labelParts = (value?.label ?? "").trim().split(/\s+/);
    const first_name = labelParts[0] ?? "";
    const last_name = labelParts.slice(1).join(" ") ?? "";
    if (onOptimisticReassign && teamData?.id && roleId) {
      onOptimisticReassign(teamData.id, roleId, value?.value, {
        first_name,
        last_name,
      });
    }

    // Emit without blocking; sync with real data when res-team-participants fires, or revert on error
    const payload = {
      participant_id: value?.value,
      role_id: roleId,
      team_id: value?.team_id,
      session_id: value?.session_id,
    };
    if (socket) {
      socket.emit("reassign-team-participant", payload, (response) => {
        if (response && response.success === false) {
          // Revert optimistic update on error
          if (onOptimisticReassign && teamData?.id && roleId) {
            onOptimisticReassign(teamData.id, roleId, previous.participantId, {
              first_name: previous.first_name,
              last_name: previous.last_name,
            });
          }
          setValue(undefined);
          if (response.error === "PARTICIPANT_ALREADY_ASSIGNED") {
            toast.error(
              response.message || "This participant is already assigned to another department.",
              { autoClose: 4000 }
            );
          } else if (response.error === "TEAM_PARTICIPANT_NOT_FOUND") {
            toast.error(
              response.message || "Team participant slot not found.",
              { autoClose: 4000 }
            );
          } else {
            toast.error(
              response.message || "Failed to assign participant. Please try again.",
              { autoClose: 4000 }
            );
          }
        }
      });
    }
  };

  const showModalDialog = (role) => {
    setShowModal(true);
    setIsAddParticipantAssign(role);
  };

  const hideModalDialog = () => {
    setShowModal(false);
  };

  // Assigned participant IDs from team data (reflects optimistic updates so dropdown stays in sync)
  const assignedFromTeamData = new Set(
    (allTeamData || [])
      .flatMap((team) => team.participant || [])
      .filter((p) => p.participant_id != null && p.is_game_leave !== true)
      .map((p) => Number(p.participant_id?.id))
      .filter((id) => !isNaN(id))
  );

  // Also include assignments from participantData (server truth) so we never show someone as available if they're assigned elsewhere
  const assignedFromParticipantData = new Set(
    participantData
      ?.filter((participant) => {
        const hasActiveAssignment = participant.team_participant?.some(
          (tp) => tp.participant_id != null && tp.is_game_leave !== true
        );
        return hasActiveAssignment;
      })
      .map((participant) => Number(participant.id))
      .filter((id) => !isNaN(id)) ?? []
  );

  const assignedParticipantIds = new Set([
    ...assignedFromTeamData,
    ...assignedFromParticipantData,
  ]);

  const options = participantData
    ?.filter((item) => !assignedParticipantIds.has(Number(item.id)))
    .map((item) => ({
      value: item.id,
      label: `${item.first_name} ${item.last_name}`,
      team_id: teamData?.id,
      session_id: teamData?.session?.id,
      current_status: teamData?.current_status,
      assign: undefined,
    }));

  const modifiedOptions = (participantData || []).map((item) => {
    // Check if participant is actively assigned (has team_participant with non-null participant_id and not game_leave)
    const activeAssignment = item.team_participant?.find(
      (tp) => tp.participant_id != null && tp.is_game_leave !== true
    );
    const teamName = activeAssignment?.team_id?.name
      ? activeAssignment.team_id.name.length > 25
        ? `${activeAssignment.team_id.name.slice(0, 25)}...`
        : activeAssignment.team_id.name
      : activeAssignment?.team_id?.team_index
        ? `Team ${activeAssignment.team_id.team_index}`
        : "";
    return {
      value: item.id,
      label: `${item.first_name} ${item.last_name}  ${
        activeAssignment ? `(${teamName})` : ""
      }`,
      team_id: teamData?.id,
      session_id: teamData?.session?.id,
      assign: activeAssignment ? false : undefined,
    };
  });

  //Custom Option Filter
  const customFilter = (option, inputValue) => {
    const optionLabelLower = option.label.toLowerCase();
    const inputValueLower = inputValue.toLowerCase();

    return optionLabelLower.includes(inputValueLower);
  };

  // Option Disabled Status
  const isOptionDisabled = (option) => option?.assign === false;

  // Handle Team Participant Removal
  const handleRemove = (item) => {
    setIsOpen(false);
    setRemoveParticipant(false);

    // Store previous for revert on error
    const previous = {
      participantId: item?.participant_id?.id ?? null,
      first_name: item?.participant_id?.first_name ?? "",
      last_name: item?.participant_id?.last_name ?? "",
    };

    // Optimistic update: clear slot in UI immediately
    if (onOptimisticUnassign && teamData?.id && item?.role_id?.id) {
      onOptimisticUnassign(teamData.id, item.role_id.id);
    }

    // Emit without blocking; sync when res-team-participants fires, or revert on error
    const payload = {
      role_id: item?.role_id?.id,
      team_id: teamData?.id,
      session_id: teamData?.session?.id,
      participant_id: item?.participant_id?.id,
    };
    if (socket) {
      socket.emit("unassign-team-participant", payload, (response) => {
        if (response && response.success === false) {
          // Revert optimistic update on error
          if (onOptimisticReassign && teamData?.id && item?.role_id?.id) {
            onOptimisticReassign(teamData.id, item.role_id.id, previous.participantId, {
              first_name: previous.first_name,
              last_name: previous.last_name,
            });
          }
          setRemoveParticipant(true);
          if (response.error === "MISSING_PARAMETERS") {
            toast.error(
              response.message || "Missing required parameters. Please try again.",
              { autoClose: 4000 }
            );
          } else if (response.error === "TEAM_PARTICIPANT_NOT_FOUND") {
            toast.error(
              response.message || "Team participant not found.",
              { autoClose: 4000 }
            );
          } else if (response.error === "TEAM_PARTICIPANT_DATA_NOT_FOUND") {
            toast.error(
              response.message || "Team participant data not found.",
              { autoClose: 4000 }
            );
          } else {
            toast.error(
              response.message || "Failed to unassign participant. Please try again.",
              { autoClose: 4000 }
            );
          }
        }
      });
    }
  };

  const handleResetGameLink = async (item) => {
    setIsOpen(false);
    await emitResetGameLink(socket, { id: Number(item.id) });
  };

  //Manage Dropdown Click Outside Behavior
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  const haldeRefreshconnection = (id) => {
    socket.emit("refresh-connection-from-client", {
      refreshConnectionIds: [id],
    });
    setOpenRefreshconnectionModel(false);
  };

  const classNameList =
    sessionData?.session?.is_open_game_link &&
    role?.current_status >= 1 &&
    role?.participant_id !== null
      ? "cursor-pointer"
      : "text-light-gray";

  const handleCancelHelpRequest = async (item) => {
    await emitCancelHelpRequest(socket, { id: Number(item.id) });
  };
  const participantName =
    role?.participant_id?.first_name + " " + role?.participant_id?.last_name ||
    "";
  return (
    <>
      <Dropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        className="select"
        isIcon={role?.current_status > 0}
        isGameLeave={role?.is_game_leave}
        isRole={role?.is_help_call_for_facilitator}
        target={
          role?.participant_id === null ? (
            <Link
              style={{ maxWidth: "80px" }}
              onClick={toggleOpen}
              className={
                role.is_help_call_for_facilitator
                  ? "text-parpal"
                  : role?.is_game_leave || role?.participant_id == null
                  ? "text-danger text-red-400-hover"
                  : "text-primary"
              }
            >
              {table?.assign}
            </Link>
          ) : (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`tooltip-${participantName}`}>
                  {participantName}
                </Tooltip>
              }
            >
              <Link
                // style={{ maxWidth: '90px' }}
                onClick={toggleOpen}
                className={`  ${
                  role.is_help_call_for_facilitator
                    ? "text-parpal"
                    : role?.is_game_leave || role?.participant_id == null
                    ? "text-danger text-red-400-hover"
                    : "text-primary"
                }`}
              >
                {participantName}
              </Link>
            </OverlayTrigger>
          )
        }
        dropdownRef={dropdownRef}
        teamData={teamData}
        isReady={role?.current_status === 2}
        isWaiting={role?.current_status === 1}
      >
        {role.is_help_call_for_facilitator && (
          <span
            className={`cursor-pointer px-3 py-1 w-full hstack mt-1 text-truncate remove-text text-parpal`}
            onClick={() => {
              handleCancelHelpRequest(role);
              setIsOpen(false);
            }}
          >
            <FaBell
              className="me-2 circle pulse mt-1"
              style={{ height: "17px", width: "15px" }}
            />
            <span className="text-truncate" style={{ width: "auto" }}>
              {" "}
              {constant?.cancelHelpRequest}
            </span>
          </span>
        )}
        <span
          onClick={() => {
            if (
              sessionData?.session?.is_open_game_link &&
              role?.current_status >= 1 &&
              role?.participant_id !== null
            ) {
              setOpenRefreshconnectionModel(!openRefreshconnectionModel);
              setIsOpen(false);
            }
          }}
          className={`${classNameList} px-3 py-1 w-full hstack mt-1 text-truncate remove-text`}
        >
          <FiRefreshCw
            className="me-2"
            style={{ height: "17px", width: "15px" }}
          />
          <span className="text-truncate" style={{ width: "auto" }}>
            {" "}
            {constant?.refreshConnection}
          </span>
        </span>
        <span
          onClick={() =>
            sessionData?.session?.is_open_game_link &&
            role?.current_status >= 1 &&
            role?.participant_id !== null
              ? handleResetGameLink(role)
              : ""
          }
          className={`${classNameList} px-3 py-1 w-full hstack mt-1 text-truncate remove-text`}
        >
          <IoMdPower
            className="me-1"
            style={{ height: "19px", width: "18px" }}
          />
          <span className="text-truncate" style={{ width: "auto" }}>
            {" "}
            {constant?.resetGameLink}
          </span>
        </span>
        {unassigned !== 0 && role?.participant_id === null && (
          <span
            onClick={() => showModalDialog(role)}
            className="drop-link px-3 py-1 w-full hstack mt-1 z-1 text-truncate"
          >
            <BsPlus className="me-2" />
            {table?.addNew}
          </span>
        )}
        {role?.participant_id !== null ? (
          <span
            onClick={() => {
              // Disable remove for co-managers (role_id > 4) when current_status is 5
              if (role?.role_id?.id > 4 && role?.current_status === 5) return;
              if (checkStatus.length > 0) {
                setRemoveParticipant(true);
                setIsOpen(false);
              } else {
                handleRemove(role);
              }
            }}
            className={
              role?.role_id?.id > 4 && role?.current_status === 5
                ? "text-light-gray px-3 py-1 w-full hstack mt-1 text-truncate"
                : "cursor-pointer px-3 py-1 w-full hstack mt-1 text-truncate remove-text "
            }
          >
            <BsXLg className="me-1" style={{ height: "17px", width: "19px" }} />
            <span className="text-truncate" style={{ width: "auto" }}>
              {" "}
              {`Remove from ${
                teamData.name ? teamData.name : `Team ${teamData.team_index}`
              }`}
            </span>
          </span>
        ) : (
          ""
        )}
        {((role?.participant_id === null && role?.current_status > 3) ||
          role?.current_status < 3) && (
          <Select
            as="select"
            options={modify || !options?.length ? modifiedOptions : options}
            filterOption={customFilter}
            onInputChange={(e) => {
              setModify(!!e);
            }}
            autoFocus
            isOpen={isOpen}
            backspaceRemovesValue={false}
            components={{ DropdownIndicator, IndicatorSeparator: null }}
            controlShouldRenderValue={false}
            isOptionDisabled={isOptionDisabled}
            hideSelectedOptions={false}
            isClearable={false}
            menuIsOpen
            onChange={onSelectChange}
            placeholder="Search..."
            value={value}
            classNamePrefix="react-select"
            classNames={{
              control: (state) =>
                state.isFocused ? "mx-3 mt-2 shadow-none" : "mx-3 mt-2",
            }}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "#152536",
              },
            })}
            styles={{
              container: (base) => ({
                ...base,
                minWidth: `263px`,
                width: `100%`,
                backgroundColor: "#FFF",
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                zIndex: "100",
              }),
              control: (base) => ({
                ...base,
                borderColor: "#cccccc",
                "&:hover": {
                  borderColor: "#cccccc",
                },
              }),
              placeholder: (base) => ({
                ...base,
                color: "#A8B5C1",
              }),
              menu: (base) => ({
                ...base,
                position: "relative",
                backgroundColor: `transparent`,
                borderRadius: 8,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                marginTop: 0,
                marginBottom: 0,
                boxShadow: `none`,
              }),
              menuList: (base) => ({
                ...base,
                maxHeight: "200px",
              }),
              option: (base) => ({
                ...base,
                lineHeight: 1,
                padding: "6px 12px",
                backgroundColor: !state?.isSelected && "white",
                color: !state?.isSelected && "#152536",
                "&:hover": {
                  backgroundColor: "#152536",
                  color: "white",
                },
              }),
            }}
          />
        )}
      </Dropdown>
      {showModal && (
        <AddParticipants
          hide={hideModalDialog}
          show={showModal}
          isAddParticipantAssign={isAddParticipantAssign}
          teamId={teamData?.id}
          socket={socket}
        />
      )}
      {removeParticipant && (
        <RemovePlayerModal
          show={removeParticipant}
          hide={() => setRemoveParticipant(false)}
          handleRemove={() => handleRemove(role)}
        />
      )}
      {openRefreshconnectionModel && (
        <RefreshConnection
          show={openRefreshconnectionModel}
          hide={() => setOpenRefreshconnectionModel(false)}
          handleRefreshConnection={() => {
            haldeRefreshconnection(role.participant_id.id);
          }}
          type="single"
        />
      )}
    </>
  );
}

const Menu = (props) => {
  return (
    <div className="pb-10 position-absolute" style={{ zIndex: 111 }}>
      <div
        className="drop-select bg-light rounded-2 pb-2"
        style={{
          boxShadow: "0px 4px 4px 0px #00000040",
          border: "1px solid #CCCCCC",
          maxWidt: "263px",
        }}
        {...props}
      />
    </div>
  );
};
const renderTooltip = (props) => (
  <Tooltip id="button-tooltip" {...props}>
    {table?.joined}
  </Tooltip>
);
const renderTooltipFirst = (props) => (
  <Tooltip id="button-tooltip" {...props}>
    {table?.notReady}
  </Tooltip>
);
const renderTooltipSecond = (props) => (
  <Tooltip id="button-tooltip" {...props}>
    {table?.ready}
  </Tooltip>
);
const renderTooltipLeave = (props) => (
  <Tooltip id="button-tooltip" {...props}>
    {constant?.leftGame}
  </Tooltip>
);
const Blanket = (props) => <div {...props} />;
const Dropdown = ({
  children,
  isOpen,
  target,
  onClose,
  dropdownRef,
  isIcon,
  isRole,
  isGameLeave,
  teamData,
  isReady,
  isWaiting,
}) => (
  <div
    style={{ maxWidth: "180px" }}
    className="overlayTrigger-tooltip"
    ref={dropdownRef}
  >
    {isGameLeave && isIcon ? (
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={isGameLeave ? renderTooltipLeave : renderTooltip}
      >
        <span className="pe-2 lh-none">
          <BsXCircle
            className="text-danger"
            size={18}
            style={{ marginTop: "-2px" }}
          />
        </span>
      </OverlayTrigger>
    ) : isIcon ? (
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={renderTooltip}
      >
        <span className="pe-2 lh-none">
          <BsFillCheckCircleFill className="text-success" size={18} />
        </span>
      </OverlayTrigger>
    ) : null}
    {!isGameLeave && isWaiting ? (
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={renderTooltipFirst}
      >
        <span className="pe-2 lh-none">
          <MdAccessTimeFilled style={{ color: "#999999" }} size={22} />
        </span>
      </OverlayTrigger>
    ) : !isGameLeave && isReady && !isRole ? (
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={renderTooltipSecond}
      >
        <span className="pe-2 lh-none">
          <BsFillCheckCircleFill className="text-success" size={18} />
        </span>
      </OverlayTrigger>
    ) : (
      isRole && (
        <FaBell
          className="text-parpal circle pulse me-2"
          style={{ height: "17px", width: "15px" }}
        />
      )
    )}
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
);
const Svg = (p) => (
  <svg
    width="0"
    height="0"
    viewBox="0 0 24 24"
    focusable="false"
    role="presentation"
    {...p}
  />
);
const DropdownIndicator = () => (
  <div>
    <Svg>
      <path
        d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </Svg>
  </div>
);
export default ParticipantsSelect;
